import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Loader2,
  Trash2,
  CheckSquare,
  X,
} from 'lucide-react';

interface BulkAction {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

interface BulkActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onDelete: () => void;
  onStatusChange?: (status: string) => void;
  statusOptions?: { value: string; label: string }[];
  onPriorityChange?: (priority: string) => void;
  priorityOptions?: { value: string; label: string }[];
  isDeleting?: boolean;
  isUpdatingStatus?: boolean;
  isUpdatingPriority?: boolean;
}

export function BulkActionBar({
  selectedCount,
  onClearSelection,
  onDelete,
  onStatusChange,
  statusOptions,
  onPriorityChange,
  priorityOptions,
  isDeleting,
  isUpdatingStatus,
  isUpdatingPriority,
}: BulkActionBarProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusValue, setStatusValue] = useState('');
  const [priorityValue, setPriorityValue] = useState('');

  if (selectedCount === 0) return null;

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    if (onStatusChange) {
      onStatusChange(value);
    }
  };

  const handlePriorityChange = (value: string) => {
    setPriorityValue(value);
    if (onPriorityChange) {
      onPriorityChange(value);
    }
  };

  return (
    <>
      <div className="fixed bottom-[76px] md:bottom-6 left-1/2 -translate-x-1/2 z-[60] animate-in slide-in-from-bottom-4 fade-in duration-200">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dyndev-green/20 bg-[var(--bg-primary)]/95 backdrop-blur-xl shadow-lg shadow-dyndev-green/5">
          <div className="flex items-center gap-2 text-sm">
            <CheckSquare size={16} className="text-dyndev-green" />
            <span className="font-medium">
              {selectedCount} selected
            </span>
          </div>

          <div className="w-px h-6 bg-[var(--border-color)]" />

          {statusOptions && onStatusChange && (
            <div className="flex items-center gap-2">
              <Select value={statusValue} onValueChange={handleStatusChange}>
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isUpdatingStatus && (
                <Loader2 size={14} className="animate-spin text-dyndev-green" />
              )}
            </div>
          )}

          {priorityOptions && onPriorityChange && (
            <div className="flex items-center gap-2">
              <Select value={priorityValue} onValueChange={handlePriorityChange}>
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isUpdatingPriority && (
                <Loader2 size={14} className="animate-spin text-dyndev-green" />
              )}
            </div>
          )}

          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isDeleting}
            className="gap-1.5 text-xs"
          >
            {isDeleting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
            Delete
          </Button>

          <button
            onClick={onClearSelection}
            className="p-1.5 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            title="Clear selection"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={18} className="text-red-400" />
              Delete {selectedCount} item{selectedCount !== 1 ? 's' : ''}?
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The selected items will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteDialogOpen(false);
                onDelete();
              }}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete {selectedCount} item{selectedCount !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
